import { observable, configure, action, flow } from 'mobx'
import API from '@aws-amplify/api'

import { ToastType, ToastTypeEnum } from '../components/Dashboard/Toasts'

configure({ enforceActions: 'always' })

interface SettingsProps {
  toasts: ToastType[]
  criteria: CriteriaObject[] | null
  isLoading: boolean
  cacheValid: boolean
  isOfficeClient: boolean
}

export const initialState: SettingsProps = {
  toasts: [],
  criteria: [],
  isLoading: false,
  cacheValid: false,
  isOfficeClient: true,
}

export interface CriteriaObject {
  industries: string[]
  headCount: string[]
  locations: string[]
  titles: string[]
  dncCompanies: string[]
  cities: string[]
  seniorityLevels: string[]
  revenues: string[]
  titleExclusions: string[]
  autoActivate: boolean
  missions: {
    firstTouch: string[]
  }
  activated: boolean
  enabled: boolean
  missionName: string
  companies: string[]
  companySearch: boolean
}
export interface DNCObject {
  leads: string[]
  domains: string[]
}

export interface Signature {
  id: string
  title: string
  signature: string
}

export class Settings implements SettingsProps {
  @observable toasts: ToastType[] = []
  @observable criteria: CriteriaObject[] | null = []
  @observable dnc: DNCObject | null = { leads: [], domains: [] }
  @observable signatures: Signature[] = []
  @observable isLoading = false
  @observable cacheValid = false
  @observable isOfficeClient = true

  constructor() {
    this.init()
  }

  @action
  init(): void {
    this.setToasts(initialState.toasts)
    this.setCriteria(initialState.criteria)
    this.setIsLoading(initialState.isLoading)
    this.setCacheValid(initialState.cacheValid)
    this.setIsOfficeClient(initialState.isOfficeClient)
  }

  @action
  pushToToasts(toast: ToastType): void {
    const newToast = toast

    if (newToast.show === undefined) {
      newToast.show = true
    }

    if (newToast.delay === undefined) {
      newToast.delay = 3000
    }

    this.setToasts(this.toasts.concat(newToast))
  }

  @action
  setIsLoading(isLoading: SettingsProps['isLoading']): void {
    this.isLoading = isLoading
  }

  @action
  popFromToasts(): void {
    const newToasts = [...this.toasts]

    newToasts.shift()
    this.setToasts(newToasts)
  }

  @action
  setToasts(toasts: SettingsProps['toasts']): void {
    this.toasts = toasts
  }

  @action
  clearState(): void {
    this.init()
  }

  @action
  setCacheValid(cacheValid: SettingsProps['cacheValid']): void {
    this.cacheValid = cacheValid
  }

  @action
  setCriteria(ownerCriteria: SettingsProps['criteria']): void {
    this.criteria = ownerCriteria
  }
  @action
  setDNC(dnc: DNCObject): void {
    const localDNC = dnc
    if (!dnc.domains || (dnc.domains && dnc.domains.length === 0)) {
      localDNC.domains = []
    }
    this.dnc = localDNC
  }

  @action
  setSignatures(signatures: Signature[]): void {
    this.signatures = signatures
  }

  @action
  handleUpdateDNC = flow(function*(this: Settings, dnc: DNCObject) {
    const payload = {
      ...dnc,
    }
    this.isLoading = true
    const myInit = { body: payload }
    try {
      yield API.put('mav-outlook-api', '/users/DNC', myInit)
    } catch (e) {
      // console.error(e)

      this.pushToToasts({
        type: ToastTypeEnum.ERROR,
        message: "Couldn't modify DNC",
      })
      this.isLoading = false
    }
    this.setDNC(dnc)
    this.isLoading = false
  })

  @action
  getCriteria = flow(function*(this: Settings) {
    try {
      this.setIsLoading(true)
      const response: CriteriaObject[] = yield API.get(
        'mav-outlook-api',
        '/V2/users/prospectCriteria',
        {},
      )

      this.setCriteria(response)
      this.setCacheValid(true)
      this.setIsLoading(false)
    } catch (e) {
      // console.log('could not fetch criteria, try again...')
      this.setIsLoading(false)
    }
  })
  @action
  getDNC = flow(function*(this: Settings) {
    try {
      this.setIsLoading(true)
      const response: DNCObject = yield API.get('mav-outlook-api', '/users/DNC', {})
      this.setDNC(response)
      this.setCacheValid(true)
      this.setIsLoading(false)
    } catch (e) {
      // console.log(e)
      // console.log('could not fetch DNC, try again...')
      this.setIsLoading(false)
    }
  })

  @action
  getSignatures = flow(function*(this: Settings) {
    try {
      this.setIsLoading(true)
      const response: Signature[] = yield API.get('mav-outlook-api', '/users/signatures', {})
      this.setSignatures(response)
      this.setCacheValid(true)
      this.setIsLoading(false)
    } catch (e) {
      // console.log(e)
      // console.log('could not fetch signatures, try again...')
      this.setIsLoading(false)
    }
  })

  @action
  setIsOfficeClient(isOfficeClient: SettingsProps['isOfficeClient']): void {
    this.isOfficeClient = isOfficeClient
  }
}

export default new Settings()
